<template>
  <div class="relative flex flex-col min-h-0 h-full overflow-visible">
    <div v-if="hasHeaders" class="grid my-3.75 flex-0" :style="gridStyle">
      <div v-for="(column, index) in columns" :key="index">
        <div class="flex flex-row justify-center">
          <IconButton
            v-if="column.headerButton"
            size="40"
            background-class="bg-button"
            @click="column.headerButton.onClick"
          >
            <icon-custom size="24" :ligature="column.headerButton.ligature" />
          </IconButton>
        </div>
      </div>
    </div>
    <div class="relative min-h-0 flex-1 overflow-visible">
      <div
        ref="scrollContainerRef"
        class="selection-table-scroll grid min-h-0 h-full overflow-y-scroll overflow-x-visible content-start"
        :style="gridContentStyle"
        v-hide-keyboard-scroll
        @scroll="onScroll"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <template v-for="cell in gridCells" :key="`${cell.columnIndex}-${cell.item}`">
          <div
            class="flex w-full content-center justify-center"
            :style="cell.rowSpan > 1 ? { gridRow: `span ${cell.rowSpan}` } : undefined"
            @click="columns[cell.columnIndex].onCellClick?.(cell.item)"
            v-long-press="
              columns[cell.columnIndex].onCellLongPress
                ? () => columns[cell.columnIndex].onCellLongPress?.(cell.item)
                : undefined
            "
          >
            <slot
              :name="`cell-${cell.columnIndex}`"
              :item="cell.item"
              :columnIndex="cell.columnIndex"
              :rowSpan="cell.rowSpan"
              :setRef="(el: any) => setCellRef(cell.columnIndex, cell.item, el)"
            />
          </div>
        </template>
      </div>

      <div
        class="absolute top-0 -right-3 z-50 w-0.75 bg-grey-1 transition-opacity duration-300 ease-out pointer-events-none"
        :class="showScrollGrip ? 'opacity-100' : 'opacity-0'"
        :style="scrollGripStyle"
      ></div>
    </div>
  </div>
</template>

<script lang="ts" setup generic="T extends string | number">
import { computed, nextTick, onUnmounted, ref, toRefs, watch } from 'vue'
import IconCustom from './IconCustom.vue'
import type { IconName } from '@/icons'
import vLongPress from '@/directives/long-press.ts'
import vHideKeyboardScroll from '@/directives/hide-keyboard-scroll.ts'
import IconButton from '@/components/IconButton.vue'

export interface ColumnConfig<T> {
  headerButton?: {
    ligature: IconName
    onClick: () => void
  }
  onCellClick?: (item: T) => void
  onCellLongPress?: (item: T) => void
  groupBy?: (item: T) => string | number
}

interface Props {
  columns: ColumnConfig<T>[]
  items: T[]
  scrollToItem?: T
}

const props = defineProps<Props>()

const { columns, items } = toRefs(props)

const emit = defineEmits<{
  (e: 'touchSwipe', payload: { columnIndex: number; item: T; swipeMode: boolean }): void
}>()

const swipeMode = ref(false)
const isSwiping = ref(false)
const cellRefs = ref<Record<number, Record<T, HTMLElement>>>({})
const scrollContainerRef = ref<HTMLElement | null>(null)
const autoScrollAnimationId = ref<number | null>(null)
const lastTouchX = ref(0)
const lastTouchY = ref(0)

const EDGE_ZONE = 60
const MAX_SCROLL_SPEED = 15
const TIME_TO_SHOW_SCROLL_GRIP = 2000
const SCROLL_GRIP_MIN_HEIGHT = 24

interface GridCell {
  columnIndex: number
  item: T
  rowSpan: number
}

const gridStyle = computed(() => ({
  gridTemplateColumns: `repeat(${columns.value.length}, 1fr)`,
  columnGap: '0.3125rem',
}))

const gridContentStyle = computed(() => ({
  ...gridStyle.value,
  rowGap: '0.625rem',
}))

const hasHeaders = computed(() => columns.value.some((c: ColumnConfig<T>) => c.headerButton))

const gridCells = computed<GridCell[]>(() => {
  const cells: GridCell[] = []
  const itemList = items.value
  const colList = columns.value

  for (let rowIndex = 0; rowIndex < itemList.length; rowIndex++) {
    const item = itemList[rowIndex]
    for (let colIndex = 0; colIndex < colList.length; colIndex++) {
      const groupBy = colList[colIndex].groupBy
      if (groupBy) {
        const groupKey = groupBy(item)
        if (rowIndex > 0 && groupBy(itemList[rowIndex - 1]) === groupKey) {
          continue
        }
        let count = 1
        while (
          rowIndex + count < itemList.length &&
          groupBy(itemList[rowIndex + count]) === groupKey
        ) {
          count++
        }
        cells.push({ columnIndex: colIndex, item, rowSpan: count })
      } else {
        cells.push({ columnIndex: colIndex, item, rowSpan: 1 })
      }
    }
  }

  return cells
})

if (props.scrollToItem != null) {
  const scrollTarget = props.scrollToItem
  const stopScrollWatch = watch(
    items,
    (newItems) => {
      if (!newItems.includes(scrollTarget)) return
      nextTick(() => {
        const container = scrollContainerRef.value
        if (!container) return
        const cellIndex = gridCells.value.findIndex((c) => c.item === scrollTarget)
        if (cellIndex < 0) return
        const el = container.children[cellIndex]
        if (el instanceof HTMLElement) {
          container.scrollTop = el.offsetTop - container.clientHeight / 2 + el.clientHeight / 2
        }
        stopScrollWatch()
      })
    },
    { immediate: true },
  )
}

const scrollGripHeight = ref(0)
const scrollGripY = ref(0)
const showScrollGrip = ref(false)
const scrollGripTimer = ref<number | null>(null)
const scrollGripStyle = computed(() => ({
  height: `${scrollGripHeight.value}px`,
  transform: `translateY(${scrollGripY.value}px)`,
}))

function calculateGripHeight(viewportHeight: number, contentHeight: number) {
  if (contentHeight <= viewportHeight) return 0
  const ratio = viewportHeight / contentHeight
  return Math.max(viewportHeight * ratio, SCROLL_GRIP_MIN_HEIGHT)
}

function updateScrollGripVisibility() {
  showScrollGrip.value = true
  if (scrollGripTimer.value !== null) {
    clearTimeout(scrollGripTimer.value)
  }
  scrollGripTimer.value = window.setTimeout(() => {
    showScrollGrip.value = false
  }, TIME_TO_SHOW_SCROLL_GRIP)
}

function onScroll() {
  const container = scrollContainerRef.value
  if (!container) return
  const viewportHeight = container.clientHeight
  const contentHeight = container.scrollHeight
  scrollGripHeight.value = calculateGripHeight(viewportHeight, contentHeight)
  const scrollMax = contentHeight - viewportHeight
  const scrollPercentage = scrollMax > 0 ? container.scrollTop / scrollMax : 0
  scrollGripY.value = scrollPercentage * (viewportHeight - scrollGripHeight.value)
  updateScrollGripVisibility()
}

onUnmounted(() => {
  stopAutoScroll()
})

function setCellRef(columnIndex: number, item: T, el: HTMLElement) {
  if (!el) return
  if (!cellRefs.value[columnIndex]) {
    cellRefs.value[columnIndex] = {} as Record<T, HTMLElement>
  }
  cellRefs.value[columnIndex][item] = el
}

function handleTouchStart(event: TouchEvent) {
  const target = event.touches?.[0]?.target as HTMLElement | undefined
  if (target && target.dataset.item && target.dataset.active !== undefined) {
    swipeMode.value = target.dataset.active !== 'true'
    isSwiping.value = true
  }
}

function handleTouchMove(event: TouchEvent) {
  const touch = event.touches[0]
  if (!touch) return

  lastTouchX.value = touch.clientX
  lastTouchY.value = touch.clientY

  if (isSwiping.value) {
    event.preventDefault()
  }

  emitSwipeAtPosition(touch.clientX, touch.clientY)

  if (isSwiping.value) {
    updateAutoScroll()
  }
}

function handleTouchEnd() {
  swipeMode.value = false
  isSwiping.value = false
  stopAutoScroll()
}

function emitSwipeAtPosition(x: number, y: number) {
  for (const [columnIndex, columnRefs] of Object.entries(cellRefs.value)) {
    for (const [item, elRef] of Object.entries(columnRefs as Record<string, HTMLElement>)) {
      if (elRef instanceof HTMLElement) {
        const rect = elRef.getBoundingClientRect()
        if (x >= rect.x && x <= rect.x + rect.width && y >= rect.y && y <= rect.y + rect.height) {
          emit('touchSwipe', {
            columnIndex: Number(columnIndex),
            item: item as T,
            swipeMode: swipeMode.value,
          })
        }
      }
    }
  }
}

function updateAutoScroll() {
  const container = scrollContainerRef.value
  if (!container) return

  const rect = container.getBoundingClientRect()
  const touchY = lastTouchY.value

  const nearTop = touchY < rect.top + EDGE_ZONE && container.scrollTop > 0
  const nearBottom =
    touchY > rect.bottom - EDGE_ZONE &&
    container.scrollTop < container.scrollHeight - container.clientHeight

  if ((nearTop || nearBottom) && autoScrollAnimationId.value === null) {
    autoScrollLoop()
  } else if (!nearTop && !nearBottom) {
    stopAutoScroll()
  }
}

function autoScrollLoop() {
  const container = scrollContainerRef.value
  if (!container || !isSwiping.value) {
    stopAutoScroll()
    return
  }

  const rect = container.getBoundingClientRect()
  const touchY = lastTouchY.value

  let scrollSpeed = 0

  if (touchY < rect.top + EDGE_ZONE) {
    const proximity = Math.max(0, touchY - rect.top) / EDGE_ZONE
    scrollSpeed = -MAX_SCROLL_SPEED * (1 - proximity)
  } else if (touchY > rect.bottom - EDGE_ZONE) {
    const proximity = Math.max(0, rect.bottom - touchY) / EDGE_ZONE
    scrollSpeed = MAX_SCROLL_SPEED * (1 - proximity)
  }

  if (scrollSpeed !== 0) {
    container.scrollTop += scrollSpeed
    emitSwipeAtPosition(lastTouchX.value, lastTouchY.value)
    autoScrollAnimationId.value = requestAnimationFrame(autoScrollLoop)
  } else {
    autoScrollAnimationId.value = null
  }
}

function stopAutoScroll() {
  if (autoScrollAnimationId.value !== null) {
    cancelAnimationFrame(autoScrollAnimationId.value)
    autoScrollAnimationId.value = null
  }
}
</script>

<style scoped>
.selection-table-scroll {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.selection-table-scroll::-webkit-scrollbar {
  display: none;
}
</style>
